import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { client } from '@/api/client';

// Suggestions are fetched server-side via the trimbleAddressSearch edge
// function, which holds ONE Pro Haul-owned Trimble key -- callers here
// never see or supply an API key. Every previous version of this
// component took an `apiKey` prop sourced from that carrier's own
// Company Settings; that per-carrier BYOK requirement is gone now that
// the key lives server-side (see trimbleAddressSearch/index.ts).
export default function TrimbleAddressAutocomplete({
  value,
  onChange,
  onSelect,
  label,
  placeholder = 'Type city and state...',
}) {
  const [input, setInput] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const justSelected = useRef(false);
  const lastConfirmedValue = useRef('');

  useEffect(() => {
    if (value !== input && !justSelected.current) {
      setInput(value || '');
      lastConfirmedValue.current = value || '';
    }
  }, [value]);

  // Debounced API call
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (justSelected.current) {
      justSelected.current = false;
      return;
    }

    if (!input || input.length < 3) {
      setSuggestions([]);
      return;
    }

    if (input === lastConfirmedValue.current) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await client.functions.invoke('trimbleAddressSearch', { query: input });
        if (cancelled) return; // a newer keystroke already superseded this request
        setSuggestions(data?.suggestions || []);
        setOpen(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Trimble search error:', err);
        setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; };
  }, [input]);

  const handleSelect = (s) => {
    justSelected.current = true;
    const confirmed = s.street || s.label;
    lastConfirmedValue.current = confirmed;
    setInput(confirmed);
    onChange({ target: { value: confirmed } });
    onSelect({ street: s.street, city: s.city, state: s.state, zip: s.zip, fullAddress: s.label });
    setSuggestions([]);
    setOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); onChange({ target: { value: e.target.value } }); }}
          onFocus={() => { if (input === lastConfirmedValue.current) { setSuggestions([]); setOpen(false); } }}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 pr-9"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown */}
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-[999] bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-white/5 transition-colors border-b border-border last:border-b-0"
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}